import * as React from "react";
import {useButton, useFocusRing} from "react-aria";
import {useOverlayTriggerState} from "react-stately";
import {ChevronUpDownIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon} from "@heroicons/react/20/solid";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import {useHover} from "@react-aria/interactions";
import {InputSetToNull} from "./Icons";
import {FieldButton} from "./DateRangePicker";
import {Popover} from "./Popover";
import {useEffect, useRef, useState} from "react";

export interface NestedItem {
    id: number | string;
    name: string;
    label: string;
    icon?: string;
    nestedAttributeDefinitions?: NestedItem[] | null;
    [key: string]: any;
}

export interface NestedSelectValue {
    parentId?: number | string;
    parentLabel?: string;
    childId: number | string;
    childLabel: string;
    path: string; // e.g., "Dokumente → Dokumentenkategorie"
}

interface NestedSelectProps {
    label?: string;
    placeholder?: string;
    isRequired?: boolean;
    isDisabled?: boolean;
    isReadOnly?: boolean;
    variant?: 'outlined' | 'embedded';
    items: NestedItem[];
    value?: NestedSelectValue | null;
    onChange?: (value: NestedSelectValue | null) => void;
    startIcon?: React.ReactNode;
    renderChevron?: boolean;
    className?: string;
    allowsSearch?: boolean;
    searchPlaceholder?: string;
    fullWidth?: boolean;
    // Allow selecting parent items (not just nested children)
    allowParentSelection?: boolean;
}

export function NestedSelect(props: NestedSelectProps) {
    const {t} = useTranslation();

    const {
        variant = 'outlined',
        label,
        isRequired,
        isDisabled,
        isReadOnly,
        placeholder,
        startIcon,
        renderChevron = true,
        className,
        items,
        value,
        onChange,
        allowsSearch = false,
        searchPlaceholder,
        fullWidth = false,
        allowParentSelection = false,
    } = props;

    // Track navigation state
    const [currentParent, setCurrentParent] = useState<NestedItem | null>(null);
    const [searchValue, setSearchValue] = useState('');
    const [triggerWidth, setTriggerWidth] = useState<number>(0);

    const state = useOverlayTriggerState({});

    const ref = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const {buttonProps, isPressed} = useButton(
        {
            onPress: () => !isDisabled && !isReadOnly && state.toggle(),
        },
        ref
    );

    const {focusProps, isFocusVisible} = useFocusRing();
    const {hoverProps, isHovered} = useHover({});

    const isEmbedded = variant === 'embedded';
    const variantClass = `field-${variant}`;
    const placeholderText = placeholder || t('components.select.placeholder');

    // Get display text for selected value
    const displayText = value ? value.path : placeholderText;

    // Filter items based on search
    const getFilteredItems = (itemsToFilter: NestedItem[]) => {
        if (!searchValue) return itemsToFilter;

        const searchLower = searchValue.toLowerCase();
        return itemsToFilter.filter(item =>
            item.label.toLowerCase().includes(searchLower) ||
            item.name.toLowerCase().includes(searchLower)
        );
    };

    // Handle item selection
    const handleSelect = (item: NestedItem, parent?: NestedItem) => {
        if (item.nestedAttributeDefinitions && item.nestedAttributeDefinitions.length > 0) {
            // This is a parent - navigate into it
            setCurrentParent(item);
            setSearchValue(''); // Clear search when navigating
        } else {
            // This is a leaf - select it
            const selectedValue: NestedSelectValue = {
                childId: item.id,
                childLabel: item.label,
                path: parent ? `${parent.label} → ${item.label}` : item.label,
                ...(parent && {
                    parentId: parent.id,
                    parentLabel: parent.label,
                }),
            };
            onChange?.(selectedValue);
            state.close();
            setCurrentParent(null);
            setSearchValue('');
        }
    };

    // Handle parent item selection (if allowed)
    const handleSelectParent = (item: NestedItem) => {
        if (!allowParentSelection) return;

        const selectedValue: NestedSelectValue = {
            childId: item.id,
            childLabel: item.label,
            path: item.label,
        };
        onChange?.(selectedValue);
        state.close();
        setCurrentParent(null);
        setSearchValue('');
    };

    // Navigate back to parent list
    const handleBack = () => {
        setCurrentParent(null);
        setSearchValue('');
    };

    // Focus search input when menu opens
    useEffect(() => {
        if (state.isOpen && allowsSearch && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 50);
        }
    }, [state.isOpen, allowsSearch]);

    // Clear search when menu closes
    useEffect(() => {
        if (!state.isOpen) {
            setSearchValue('');
            setCurrentParent(null);
        }
    }, [state.isOpen]);

    // Measure trigger width when component mounts or when popover opens
    useEffect(() => {
        if (ref.current) {
            const updateWidth = () => {
                if (ref.current) {
                    const inputElement = ref.current as HTMLInputElement;
                    const width = inputElement.offsetWidth;
                    if (width) {
                        setTriggerWidth(width);
                    }
                }
            };

            updateWidth();
            if (state.isOpen) updateWidth();

            window.addEventListener('resize', updateWidth);
            return () => window.removeEventListener('resize', updateWidth);
        }
    }, [state.isOpen]);

    // Determine which items to show
    const itemsToShow = currentParent
        ? currentParent.nestedAttributeDefinitions || []
        : items;

    const filteredItems = getFilteredItems(itemsToShow);

    return (
        <div className={clsx(
            'inline-flex flex-col relative min-w-[180px]',
            {'w-full': fullWidth},
            className
        )}>
            {label && (
                <label className={`field-label ${isDisabled ? 'field-disabled' : ''}`}>
                    {label}
                    {isRequired && <span className={'field-required'}>*</span>}
                </label>
            )}

            <button
                {...buttonProps}
                {...hoverProps}
                {...focusProps}
                ref={ref}
                disabled={isDisabled}
                className={clsx(
                    'field-base',
                    variantClass,
                    'items-center justify-between',
                    isFocusVisible && 'border-pink-500',
                    !isFocusVisible && 'border-gray-300',
                    state.isOpen ? 'bg-gray-100' : 'bg-inherit',
                    isHovered && 'field-hovered',
                    isDisabled && 'field-disabled',
                    isEmbedded && isPressed && 'field-pressed'
                )}
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {startIcon && <span className="flex-shrink-0">{startIcon}</span>}
                    <span className={clsx(
                        `${variantClass} sm:text-sm/6 truncate`,
                        !value && 'text-gray-400'
                    )}>
                        {displayText}
                    </span>
                </div>

                <div className="flex items-center space-x-1">
                    {!isDisabled && !isReadOnly && value && (
                        <FieldButton
                            onPress={(e: any) => {
                                e.stopPropagation();
                                onChange?.(null);
                            }}
                            aria-label="Reset selection"
                        >
                            <InputSetToNull className="text-gray-700 w-3.5 h-3.5"/>
                        </FieldButton>
                    )}

                    {(!isReadOnly && renderChevron) && (
                        <ChevronUpDownIcon className={`w-5 h-5 flex-shrink-0 ${
                            isFocusVisible ? "text-pink-500" : "text-gray-500"
                        }`}/>
                    )}

                    {isReadOnly && (
                        <span className={'text-xs text-gray-500 rounded-md bg-gray-200 px-2 py-1'}>
                            {t('components.select.readOnly')}
                        </span>
                    )}
                </div>
            </button>

            {!isReadOnly && state.isOpen && (
                <Popover state={state} triggerRef={ref} placement="bottom start">
                    <div className="flex flex-col bg-white rounded-md" style={{minWidth: triggerWidth ? `${triggerWidth}px` : 'auto'}}>
                        {/* Breadcrumb / Back Navigation */}
                        {currentParent && (
                            <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
                                <button
                                    onClick={handleBack}
                                    className="flex items-center gap-1 text-sm text-pink-600 hover:text-pink-700 font-medium"
                                >
                                    <ChevronLeftIcon className="w-4 h-4"/>
                                    <span>{currentParent.label}</span>
                                </button>
                            </div>
                        )}

                        {/* Search */}
                        {allowsSearch && (
                            <div className="p-2 border-b border-gray-200 bg-inherit sticky top-0 z-10">
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        placeholder={searchPlaceholder || t('components.select.searchPlaceholder')}
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Items List */}
                        <div className="overflow-auto">
                            {filteredItems.length === 0 ? (
                                <div className="px-3 py-8 text-center text-sm text-gray-500">
                                    {searchValue
                                        ? t('components.select.noResults')
                                        : t('components.select.noItems')
                                    }
                                </div>
                            ) : (
                                <ul className="py-1">
                                    {filteredItems.map((item) => {
                                        const hasChildren = item.nestedAttributeDefinitions && item.nestedAttributeDefinitions.length > 0;

                                        return (
                                            <li key={item.id}>
                                                <button
                                                    onClick={() => {
                                                        handleSelect(item, currentParent || undefined);
                                                    }}
                                                    className={clsx(
                                                        'w-full px-3 py-2 bg-[rgba(255,255,255,0.95)] text-left text-sm flex items-center justify-between',
                                                        'hover:bg-gray-100 transition-colors',
                                                        'focus:outline-none focus:bg-gray-100',
                                                    )}
                                                >
                                                    <span className="flex-1">{item.label}</span>
                                                    {hasChildren && (
                                                        <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0"/>
                                                    )}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                </Popover>
            )}
        </div>
    );
}